<template>
  <div class="generation-error-logs">
    <v-container fluid>
      <v-toolbar color="primary" class="header-title text-white">
        <v-toolbar-title class="title">{{ $t('generationErrorLogsTitle') }}</v-toolbar-title>
        <v-spacer />
        <div class="d-flex pr-4">
          <v-toolbar-title class="title">{{ today }}</v-toolbar-title>
        </div>
      </v-toolbar>

      <v-card class="logs-card" elevation="2">
        <v-tabs
          v-model="activeTab"
          color="primary"
          slider-color="primary"
          class="source-tabs"
          show-arrows
          @update:model-value="onTabChanged"
        >
          <v-tab v-for="tab in tabs" :key="tab.value" :value="tab.value">
            {{ $t(tab.titleKey) }}
          </v-tab>
        </v-tabs>

        <v-divider />

        <v-card-text>
          <v-row align="center" class="mb-2">
            <v-col cols="12" md="6">
              <v-text-field
                v-model="searchText"
                :label="$t('generationErrorLogsSearchPlaceholder')"
                prepend-inner-icon="mdi-magnify"
                variant="outlined"
                density="comfortable"
                clearable
                hide-details
                @keydown.enter="search"
              />
            </v-col>
            <v-col cols="12" md="6" class="d-flex justify-end ga-2">
              <v-select
                v-model="statusFilter"
                :items="statusOptions"
                :label="$t('generationErrorLogsStatus')"
                variant="outlined"
                density="comfortable"
                clearable
                hide-details
                class="status-filter"
                @update:model-value="search"
              />
              <v-btn
                color="primary"
                variant="outlined"
                prepend-icon="mdi-refresh"
                :loading="loading"
                @click="search"
              >
                {{ $t('generationErrorLogsRefresh') }}
              </v-btn>
            </v-col>
          </v-row>

          <div class="table-scroll" tabindex="0" :aria-label="$t('generationErrorLogsTableAriaLabel')">
            <v-data-table-server
              v-model:options="options"
              :headers="headers"
              :items="groups"
              :items-length="totalItems"
              :items-per-page="itemsPerPage"
              :loading="loading"
              hover
              @click:row="openRow"
            >
              <template #item.sourceType="{ item }">
                <v-chip size="small" color="primary" variant="tonal">
                  {{ sourceLabel(item.sourceType) }}
                </v-chip>
              </template>
              <template #item.sourceName="{ item }">
                <span class="source-name">{{ item.sourceName || item.sourceId || '-' }}</span>
              </template>
              <template #item.counts="{ item }">
                <span>{{ item.totalCount ?? '-' }} / {{ item.successCount ?? '-' }} / {{ item.failedCount }}</span>
              </template>
              <template #item.status="{ item }">
                <v-chip :color="statusColor(item.status)" size="small" variant="tonal">
                  {{ statusLabel(item.status) }}
                </v-chip>
              </template>
              <template #item.startedAt="{ item }">
                {{ formatDate(item.startedAt) }}
              </template>
              <template #item.actions="{ item }">
                <v-btn
                  size="small"
                  variant="text"
                  color="primary"
                  append-icon="mdi-chevron-right"
                  @click.stop="openDetails(item)"
                >
                  {{ $t('generationErrorLogsView') }}
                </v-btn>
              </template>
              <template #no-data>
                <div class="empty-state">
                  <v-icon size="48" color="grey-lighten-1">mdi-check-circle-outline</v-icon>
                  <div class="text-subtitle-1 mt-2">{{ $t('generationErrorLogsEmptyTitle') }}</div>
                  <div class="text-body-2 text-medium-emphasis">{{ $t('generationErrorLogsEmptyDescription') }}</div>
                </div>
              </template>
            </v-data-table-server>
          </div>

          <div v-if="loadError" class="error-message mt-4" role="alert" aria-live="polite">
            <v-icon class="mr-2">mdi-alert-circle-outline</v-icon>
            {{ loadError }}
          </div>
        </v-card-text>
      </v-card>
    </v-container>

    <v-navigation-drawer
      v-model="drawerOpen"
      location="right"
      temporary
      :width="drawerWidth"
      class="details-drawer"
    >
      <v-card v-if="selectedGroup" flat class="h-100 d-flex flex-column">
        <v-card-title class="d-flex align-center pa-4">
          <span>{{ $t('generationErrorLogsDetailsTitle') }}</span>
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" :aria-label="$t('generationErrorLogsCloseDetails')" @click="drawerOpen = false" />
        </v-card-title>
        <v-divider />
        <v-card-text class="flex-grow-1 overflow-y-auto">
          <div class="detail-summary">
            <div><span>{{ $t('generationErrorLogsSource') }}</span><strong>{{ sourceLabel(selectedGroup.sourceType) }}</strong></div>
            <div><span>{{ $t('generationErrorLogsTarget') }}</span><strong>{{ selectedGroup.sourceName || selectedGroup.sourceId || '-' }}</strong></div>
            <div><span>{{ $t('generationErrorLogsOperation') }}</span><strong>{{ selectedGroup.operation || '-' }}</strong></div>
            <div><span>{{ $t('generationErrorLogsFailed') }}</span><strong>{{ selectedGroup.failedCount }}</strong></div>
          </div>

          <div class="text-subtitle-1 font-weight-medium mt-6 mb-2">{{ $t('generationErrorLogsFailedItems') }}</div>
          <v-table density="comfortable" class="failure-table">
            <thead>
              <tr>
                <th>{{ $t('generationErrorLogsItem') }}</th>
                <th>{{ $t('generationErrorLogsPhase') }}</th>
                <th>{{ $t('generationErrorLogsError') }}</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="failure in selectedGroup.failures" :key="failure.failureId">
                <tr>
                  <td>{{ failure.itemName || failure.itemId || '-' }}</td>
                  <td>{{ failure.phase || '-' }}</td>
                  <td class="error-cell">{{ failure.errorMessage || '-' }}</td>
                </tr>
                <tr v-if="failure.technicalDetails">
                  <td colspan="3" class="technical-cell">
                    <v-expansion-panels variant="accordion">
                      <v-expansion-panel :title="$t('generationErrorLogsTechnicalDetails')">
                        <v-expansion-panel-text>
                          <pre>{{ failure.technicalDetails }}</pre>
                        </v-expansion-panel-text>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </td>
                </tr>
              </template>
            </tbody>
          </v-table>
        </v-card-text>
      </v-card>
    </v-navigation-drawer>
  </div>
</template>

<script>
export default {
  name: 'GenerationErrorLogs',
  data() {
    return {
      today: new Date().toLocaleDateString('en-GB', {
        day: '2-digit', month: '2-digit', year: 'numeric'
      }),
      tabs: [
        { titleKey: 'generationErrorLogsAll', value: 'ALL' },
        { titleKey: '_Documents', value: 'Document' },
        { titleKey: '_MetaDoc', value: 'MetaDocument' },
        { titleKey: '_CommunityManagement', value: 'Community' },
        { titleKey: '_MetaCommunityManagement', value: 'MetaCommunity' },
      ],
      activeTab: 'ALL',
      searchText: '',
      statusFilter: null,
      groups: [],
      totalItems: 0,
      itemsPerPage: 10,
      options: { page: 1, itemsPerPage: 10 },
      loading: false,
      loadError: '',
      drawerOpen: false,
      selectedGroup: null,
    };
  },
  computed: {
    headers() {
      return [
        { title: this.$t('generationErrorLogsSource'), key: 'sourceType', sortable: false },
        { title: this.$t('generationErrorLogsTarget'), key: 'sourceName', sortable: false },
        { title: this.$t('generationErrorLogsOperation'), key: 'operation', sortable: false },
        { title: this.$t('generationErrorLogsCounts'), key: 'counts', sortable: false, align: 'center' },
        { title: this.$t('generationErrorLogsStatus'), key: 'status', sortable: false, align: 'center' },
        { title: this.$t('generationErrorLogsStarted'), key: 'startedAt', sortable: false },
        { title: '', key: 'actions', sortable: false, align: 'end' },
      ];
    },
    statusOptions() {
      return [
        { title: this.$t('generationErrorLogsCompletedWithErrors'), value: 'CompletedWithErrors' },
        { title: this.$t('generationErrorLogsFailed'), value: 'Failed' },
      ];
    },
    drawerWidth() {
      return window.innerWidth < 700 ? '100%' : 620;
    },
  },
  watch: {
    options: {
      handler() { this.fetchLogs(); },
      deep: true,
    },
    '$route.query.sourceType': {
      handler(value) {
        if (value && this.tabs.some(tab => tab.value === value)) {
          this.activeTab = value;
          this.options.page = 1;
          this.fetchLogs();
        }
      },
    },
  },
  mounted() {
    const sourceType = this.$route.query.sourceType;
    if (sourceType && this.tabs.some(tab => tab.value === sourceType)) this.activeTab = sourceType;
    this.fetchLogs();
  },
  methods: {
    onTabChanged(value) {
      this.options.page = 1;
      this.fetchLogs();
    },
    search() {
      this.options.page = 1;
      this.fetchLogs();
    },
    fetchLogs() {
      this.loading = true;
      this.loadError = '';
      const request = {
        page: this.options.page || 1,
        perPage: this.options.itemsPerPage || this.itemsPerPage,
        sourceType: this.activeTab,
        status: this.statusFilter || null,
        search: this.searchText || null,
      };
      this.$call.SearchGenerationErrorLogs(request, (response) => {
        if (response.status === 0) {
          this.groups = response.data?.data || [];
          this.totalItems = response.data?.total || 0;
        } else {
          this.groups = [];
          this.totalItems = 0;
          this.loadError = response.message || this.$t('generationErrorLogsLoadError');
        }
        this.loading = false;
      }, () => {
        this.groups = [];
        this.totalItems = 0;
        this.loading = false;
        this.loadError = this.$t('generationErrorLogsLoadError');
      });
    },
    openRow(_event, row) {
      if (row?.item) this.openDetails(row.item);
    },
    openDetails(item) {
      this.selectedGroup = item;
      this.drawerOpen = true;
      this.$call.GetGenerationErrorLog(item.failureGroupId, (response) => {
        if (response.status === 0) this.selectedGroup = response.data;
      }, () => {
        this.loadError = this.$t('generationErrorLogsDetailsLoadError');
      });
    },
    sourceLabel(sourceType) {
      const match = this.tabs.find(tab => tab.value === sourceType);
      return match ? this.$t(match.titleKey) : sourceType || '-';
    },
    statusLabel(status) {
      if (status === 'CompletedWithErrors') return this.$t('generationErrorLogsCompletedWithErrors');
      if (status === 'Failed') return this.$t('generationErrorLogsFailed');
      return status || this.$t('generationErrorLogsFailed');
    },
    statusColor(status) {
      return status === 'CompletedWithErrors' ? 'warning' : 'error';
    },
    formatDate(value) {
      if (!value) return '-';
      return new Date(value).toLocaleString();
    },
  },
};
</script>

<style scoped>
.generation-error-logs { background: #f7f8fb; min-height: 100%; }
.header-title { border-radius: 12px; margin-bottom: 24px; }
.logs-card { border-radius: 16px; overflow: hidden; }
.source-tabs { min-height: 72px; padding: 0 16px; }
.source-tabs :deep(.v-tab) { letter-spacing: 0.12em; font-size: 0.78rem; min-width: auto; margin-right: 16px; }
.table-scroll { overflow-x: auto; }
.source-name { font-weight: 500; }
.status-filter { max-width: 230px; }
.empty-state { padding: 48px 16px; text-align: center; }
.error-message { display: flex; align-items: center; color: rgb(var(--v-theme-error)); }
.details-drawer { max-width: 100vw; }
.detail-summary { display: grid; gap: 12px; grid-template-columns: repeat(2, minmax(0, 1fr)); }
.detail-summary div { background: #f5f6fa; border-radius: 8px; padding: 12px; }
.detail-summary span { display: block; color: #6b7280; font-size: 0.75rem; margin-bottom: 4px; }
.detail-summary strong { overflow-wrap: anywhere; }
.failure-table { min-width: 520px; }
.error-cell { color: rgb(var(--v-theme-error)); }
.technical-cell { padding: 0 !important; }
.technical-cell pre { max-height: 260px; overflow: auto; white-space: pre-wrap; word-break: break-word; }
@media (max-width: 700px) {
  .source-tabs { padding: 0 4px; }
  .source-tabs :deep(.v-tab) { margin-right: 4px; font-size: 0.68rem; }
  .detail-summary { grid-template-columns: 1fr; }
  .status-filter { max-width: none; flex: 1; }
}
</style>
